import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import i18n from "i18next";

import Dashboard from "./pages/Dashboard";
import AuthLayout from "./components/Layout/AuthLayout";
import Login from "./pages/auth2/Login";
import Register from "./pages/auth2/Register";
import Blank from "./pages/Blank";
import Settings from "./pages/Settings";
import ManageAccount from "./pages/ManageAccount";
import ChangePassword from "./pages/change-password";
import Language from "./pages/language";
import StripeConnect from "./pages/stripe-connect";
import BlockedUsers from "./pages/blocked-users";
import VibeMembers from "./components/VibeMembers";
import FirebaseNotificationTester from "./components/FirebaseNotificationTester";

import { Route, Routes, Navigate, useNavigate } from "react-router-dom";
import Verification from "./pages/VerificationPage";
import HomePage from "./pages/HomePage";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "react-toastify/dist/ReactToastify.css";

import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import UserNotifications from "./pages/UserNotification";
import CreatePoll from "./pages/CreatePoll";
import PollsList from "./pages/PollsList";
import PollDetails from "./pages/PollDetails";
import CreateVibe from "./pages/CreateVibe/CreateVibe";
import Favorites from "./pages/Favorites";
import MyVibe from "./pages/MyVibe";
import Subscription from "./pages/Subscription";
import VibeDetail from "./pages/VibeDetail";
import MyLinks from "./pages/MyLinks";
import AddIndividual from "./pages/CreateVibe/AddIndividual";
import Step2LinkVibe from "./pages/CreateVibe/Step2LinkVibe";
import CalendarPage from "./pages/Calender";
import VibesBucks from "./pages/VibesBucks";
import ForgotPassword from "./pages/auth2/ForgotPassword";
import OTPVerification from "./pages/auth2/OtpVerification";
import ResetPassword from "./pages/auth2/ResetPassword";
import VerificationSuccess from "./pages/auth2/verifocation";
import axios from "axios";
import i18next from "i18next";
import { useEffect, useState } from "react";
// ...existing code...
import { getAppSettings } from "./services/userService";
import UnActiveSubscription from "./components/UnActiveSubscription";
import { useFirebaseNotifications } from "./hooks/useFirebaseNotifications";
import secureStorage from "./utils/secureStorage";
import ErrorBoundary from "./components/ErrorBoundary";

function App() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const { initialize: initializeNotifications, startListening } =
    useFirebaseNotifications();

  useEffect(() => {
    const fetchSettings = async () => {
      // Only fetch settings if user is authenticated
      if (!isAuthenticated) {
        return;
      }
      
      try {
        const settings = await getAppSettings();
        // save to secure storage
        if (settings) {
          secureStorage.setJSON("appSettings", settings);
        }
      } catch (error) {
        // Silently fail - app can work without settings
        // Settings will be fetched when user logs in or on next page load
        console.warn("Failed to fetch app settings:", error.message);
      }
    };
    fetchSettings();
  }, [isAuthenticated]);

  useEffect(() => {
    const reqInterceptor = axios.interceptors.request.use((config) => {
      // add common headers once
      // Attach FCM token if available
      try {
        const fcmToken = secureStorage.getItem("fcmToken");
        if (fcmToken) {
          config.headers["FCM-Token"] = fcmToken;
        }
      } catch (e) {
        // ignore storage errors
      }

      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (userTimeZone) {
        config.headers["Timezone"] = userTimeZone;
      }
      config.headers["App-Version"] = process.env.REACT_APP_VERSION || "1.2";
      const language = i18next.language === "sp" ? "es" : i18next.language;
      config.headers["Accept-Language"] = language || "en";
      config.headers["Device-Type"] = "W";
      config.headers["Device-Token"] = "Device token";
      config.headers["Device-Name"] = navigator.userAgent || "Web Browser";
      return config;
    });

    const resInterceptor = axios.interceptors.response.use(
      (response) => {
        // If backend signals subscription required, open modal
        const data = response?.data;
        const isSubscriptionRequired =
          data &&
          data.success === false &&
          data.status === 402 &&
          typeof data.message === "string" &&
          data.message.toLowerCase().includes("haven't subscribed");
        if (isSubscriptionRequired) {
          // show modal to prompt user to subscribe
          try {
            setShowSubscriptionModal(true);
          } catch (e) {
            // no-op if state not available
          }
        }
        return response;
      },
      (error) => {
        // Check error response payload too
        const errData = error?.response?.data;
        const isSubscriptionRequiredErr =
          errData &&
          errData.success === false &&
          errData.status === 402 &&
          typeof errData.message === "string" &&
          errData.message.toLowerCase().includes("haven't subscribed");
        if (isSubscriptionRequiredErr) {
          try {
            setShowSubscriptionModal(true);
          } catch (e) {
            // ignore
          }
        }
        // Centralized error handling is done in axiosInterceptors.js
        return Promise.reject(error);
      }
    );

    return () => {
      // eject interceptors on unmount to avoid duplicate handlers
      axios.interceptors.request.eject(reqInterceptor);
      axios.interceptors.response.eject(resInterceptor);
    };
    // run once on mount
  }, []);

  // Initialize Firebase notifications (always, not just when authenticated)
  useEffect(() => {
    const initFirebase = async () => {
      try {
        // Initialize Firebase notifications regardless of auth status for testing
        await initializeNotifications();
        startListening();
      } catch (error) {
      }
    };

    initFirebase();
  }, [initializeNotifications, startListening]); // Removed isAuthenticated dependency

  const Protected = ({ isLoggedIn, children }) => {
    const storedUser = secureStorage.getJSON("user");
    if (!isLoggedIn || !storedUser) {
      return <Navigate to="/login" replace />;
    }
    if (storedUser) {
      // 1. Check email verification
      if (storedUser.is_email_verified === false) {
        window.location.href = "/otp?type=email_verified";
        return;
      }
    }

    return children;
  };
  const handleAddMembers = (selectedUsers) => {
    // Save selected users to context/state if needed
    navigate("/create-vibe");
  };

  const savedLang = secureStorage.getItem("appLanguage");
  if (savedLang) {
    i18n.changeLanguage(savedLang);
  }

  // Move language initialization into effect to avoid triggering re-renders during render
  useEffect(() => {
    const saved = secureStorage.getItem("appLanguage");
    if (saved) i18n.changeLanguage(saved);
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <ErrorBoundary>
          <ToastContainer position="top-right" autoClose={1000} />

          <UnActiveSubscription
            showSubscriptionModal={showSubscriptionModal}
            setShowSubscriptionModal={setShowSubscriptionModal}
          />
        <Routes>
        <Route path="/" element={<AuthLayout />}>
          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <Dashboard />
              </Protected>
            }
          />
          <Route
            path="/create-poll"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <CreatePoll />
              </Protected>
            }
          />
          <Route
            path="/polls-list"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <PollsList />
              </Protected>
            }
          />
          <Route
            path="/polls-details/:id"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <PollDetails />
              </Protected>
            }
          />
          <Route
            path="/create-vibe"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <CreateVibe />
              </Protected>
            }
          />
          <Route
            path="/favorite"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <Favorites />
              </Protected>
            }
          />
          <Route
            path="/my-vibes"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <MyVibe />
              </Protected>
            }
          />
          <Route
            path="/vibe/:id"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <VibeDetail />
              </Protected>
            }
          />
          <Route
            path="/vibe-members"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <VibeMembers />
              </Protected>
            }
          />
          <Route
            path="/subscription"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <Subscription />
              </Protected>
            }
          />
          <Route
            path="/firebase-test"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <FirebaseNotificationTester />
              </Protected>
            }
          />
          <Route
            path="/my-links"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <MyLinks />
              </Protected>
            }
          />
          <Route
            path="/link-vibe"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <Step2LinkVibe />
              </Protected>
            }
          />
          <Route
            path="/add-individual"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <AddIndividual
                  onAdd={handleAddMembers}
                  onBack={() => navigate("/create-vibe?step=2")}
                />
              </Protected>
            }
          />
          <Route
            path="/calendar"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <CalendarPage />
              </Protected>
            }
          />
          <Route
            path="/vibes-bucks"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <VibesBucks />
              </Protected>
            }
          />

          <Route
            path="/notifications"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <UserNotifications />
              </Protected>
            }
          />
          <Route
            path="/settings"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <Settings />
              </Protected>
            }
          />
          <Route
            path="/profile"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <ManageAccount />
              </Protected>
            }
          />
          <Route
            path="/change-password"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <ChangePassword />
              </Protected>
            }
          />
          <Route
            path="/language"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <Language />
              </Protected>
            }
          />
          <Route
            path="/stripe-connect"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <StripeConnect />
              </Protected>
            }
          />
          <Route
            path="/blocked-users"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <BlockedUsers />
              </Protected>
            }
          />
          <Route
            path="/blank"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <Blank />
              </Protected>
            }
          />
          <Route
            path="/home"
            element={
              <Protected isLoggedIn={isAuthenticated}>
                <HomePage />
              </Protected>
            }
          />
          {/* Public routes */}
          <Route path="/auth/login" element={<Login />} />
          <Route path="/auth/verification" element={<Verification />} />

          <Route path="/auth/register" element={<Register />} />
          <Route path="/auth/forgot-password" element={<Register />} />
          <Route path="/" element={<Navigate to="/home" replace />} />
        </Route>
        <Route path="/terms&condition" element={<TermsAndConditions />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />

        <Route path="/login" element={<Login />} />
        <Route path="/otp" element={<OTPVerification />} />
        <Route path="/verification" element={<VerificationSuccess />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
      </Routes>
        </ErrorBoundary>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
